/**
 * Route delay *formatting* only.
 *
 * Guards against absurd values (e.g. 24528 minutes) from timestamp/parsing bugs: when the route
 * delay is outside ±24h, the worst stop delay is used instead so the UI shows something sensible.
 *
 * Classification — is this route late, does it need attention, what do we call that — lives in
 * @/lib/statusDisplay. Keeping the two apart avoids an import cycle, since statusDisplay depends
 * on the helpers below.
 */

const MAX_REASONABLE_DELAY_MINUTES = 24 * 60 // 24 hours

export interface RouteWithDelayAndStops {
  delayMinutes: number
  stops?: Array<{ delayMinutes?: number }>
}

/**
 * Formats a number of minutes into a human-friendly duration string.
 * Examples:
 *   5  -> "5 min"
 *   90 -> "1h 30m"
 *   194 -> "3h 14m"
 *   0  -> "0 min"
 * When `short` is true (default false), uses compact form even below 60: "5m" instead of "5 min".
 */
export function formatDuration(minutes: number, short = false): string {
  const abs = Math.abs(minutes)
  if (abs < 60) {
    return short ? `${abs}m` : `${abs} min`
  }
  const h = Math.floor(abs / 60)
  const m = abs % 60
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

/**
 * Returns a delay value safe for display. If the stored route delay is absurd
 * (e.g. > 24h from a timestamp bug), falls back to the stop with the largest
 * absolute delay so the route shows e.g. "103 min late" instead of "24528 min late".
 */
export function getDisplayRouteDelayMinutes(route: RouteWithDelayAndStops): number {
  const d = route.delayMinutes ?? 0
  if (Math.abs(d) <= MAX_REASONABLE_DELAY_MINUTES) return d
  const stops = route.stops ?? []
  if (stops.length > 0) {
    const maxStop = stops.reduce((best, s) => {
      const sd = s.delayMinutes ?? 0
      return Math.abs(sd) > Math.abs(best) ? sd : best
    }, 0)
    return maxStop
  }
  return 0
}
